/**
 * Cursor pagination helpers matching `kernel/api/pagination.py`.
 *
 * The backend uses DRF `CursorPagination`: default page size 25, max 100, ordered
 * `-created_at`. `next`/`previous` are absolute URLs with an opaque `cursor` value.
 * The cursor is not a page number and not an offset — it encodes a position in the
 * ordering, so it must be round-tripped verbatim.
 */

import type { CursorPage } from './types';

/** Pull the opaque `cursor` value out of a `next`/`previous` URL. */
export function extractCursor(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    // The URL is absolute in production but may be relative behind a proxy.
    const parsed = new URL(url, 'http://placeholder.invalid');
    return parsed.searchParams.get('cursor') ?? undefined;
  } catch {
    return undefined;
  }
}

export const nextCursor = <T>(page: CursorPage<T> | undefined) => extractCursor(page?.next);
export const previousCursor = <T>(page: CursorPage<T> | undefined) =>
  extractCursor(page?.previous);

/** Flatten infinite-query pages into one array. */
export function flattenPages<T>(pages: CursorPage<T>[] | undefined): T[] {
  return pages?.flatMap((page) => page.results) ?? [];
}

/** The API caps `limit` at 100; asking for more is a 400, not a silent clamp. */
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;

export function clampLimit(limit: number | undefined): number | undefined {
  if (limit === undefined) return undefined;
  return Math.min(Math.max(1, Math.trunc(limit)), MAX_PAGE_SIZE);
}
